import ImagePlaceholder from "assets/icons/imagePlaceholder.svg"
import { DetailedProductFieldsFragment } from "client/graphql/storefront-api"
import { Dispatch, SetStateAction } from "react"
import { FormattedMessage } from "react-intl"
import { buttonFocus } from "shared/styles/mixins"
import styled from "styled-components"

import { getDefaultVariantForProduct } from "../../utils/getDefaultVariantForProduct"
import { Price } from "../ui/priceComponents/Price"
import { ProductTitle } from "../ui/textStyles"

export const MultiProductTileImageWrapper = styled.div<{ $src?: string }>`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;

  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: ${(props) => `url(${props.$src})`};

  &[data-featured="true"] {
    border-radius: 0;
  }

  svg {
    margin: 0 auto;
    width: 100%;
    height: 100%;
  }
`

export const TileTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export const Tile = styled.button`
  border: none;
  background-color: transparent;
  appearance: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  border-radius: 8px;
  padding: 8px;

  transition: all 0.15s ease-in;

  ${buttonFocus};

  &:hover:not(:disabled),
  &:focus-visible {
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.07),
      0 2px 4px rgba(0, 0, 0, 0.07),
      0 4px 8px rgba(0, 0, 0, 0.07),
      0 8px 16px rgba(0, 0, 0, 0.07),
      0 16px 32px rgba(0, 0, 0, 0.07),
      0 32px 64px rgba(0, 0, 0, 0.07);
    transform: scale(1.03, 1.03);

    img {
      transform: scale(1.05, 1.05);
    }
  }

  img {
    transition: transform 0.15s ease-in;
  }

  &[data-featured="true"] {
    margin: 0 8px 8px;
    padding: 0;
    display: flex;
    flex-direction: row-reverse;
    box-shadow:
      0 0px 2px rgba(0, 0, 0, 0.07),
      0 1px 3px rgba(0, 0, 0, 0.07),
      0 4px 8px rgba(0, 0, 0, 0.07),
      0 2px 4px rgba(0, 0, 0, 0.07),
      0 16px 32px rgba(0, 0, 0, 0.07),
      0 3px 5px rgba(0, 0, 0, 0.07);

    & > * {
      width: 50%;
      aspect-ratio: 1 / 1;
    }

    & > *:nth-child(2) {
      padding: 16px;
      display: flex;
      justify-content: flex-end;
      flex-direction: column;
    }

    &:hover:not(:disabled),
    &:focus-visible {
      transform: scale(1.03, 1.03);
      background-color: white;
      box-shadow:
        0 1px 2px rgba(0, 0, 0, 0.07),
        0 2px 4px rgba(0, 0, 0, 0.07),
        0 4px 8px rgba(0, 0, 0, 0.07),
        0 8px 16px rgba(0, 0, 0, 0.07),
        0 16px 32px rgba(0, 0, 0, 0.07),
        0 32px 64px rgba(0, 0, 0, 0.07);
    }

    @media (min-width: 343px) {
      grid-column: 1 / span 2;
    }
  }
`

export const TileFeatured = styled.p`
  font-size: 0.86rem;
  text-transform: uppercase;
  font-weight: 300;
  margin: 0;
  text-align: left;
  margin-bottom: auto;
  color: ${(props) => props.theme.text.dark};
`

export const TileTitle = styled(ProductTitle)`
  line-height: 1.3;
  font-size: 1rem;
  margin-bottom: 2px;
`

export const TilePriceWrapper = styled.div`
  width: 100%;
  text-align: left;
`

export type MultiProductTileProps = {
  product: DetailedProductFieldsFragment
  setCurrentProductId?: Dispatch<SetStateAction<string>>
  isFeatured?: boolean
}

const MultiProductTile = ({
  product,
  setCurrentProductId = () => {},
  isFeatured = false,
}: MultiProductTileProps) => {
  const variantToShow = getDefaultVariantForProduct(product)
  const imageUrl: string | undefined = product.images.nodes[0]?.url
  return (
    <Tile
      onClick={() => setCurrentProductId(product.id)}
      key={product.id}
      data-featured={isFeatured}
      type="button"
    >
      <MultiProductTileImageWrapper
        $src={imageUrl}
        aria-label={product.title}
        data-featured={isFeatured}
      >
        {imageUrl === undefined && <ImagePlaceholder />}
      </MultiProductTileImageWrapper>

      <TileTextWrapper>
        {isFeatured && (
          <TileFeatured>
            <FormattedMessage
              id="multiproductTile.featured"
              defaultMessage="Featured"
            />
          </TileFeatured>
        )}
        <TileTitle $maxLines={isFeatured ? 3 : 2}>
          {product.title}
        </TileTitle>
        <TilePriceWrapper>
          <Price
            currencyCode={variantToShow.price.currencyCode}
            showCurrencyCode={false}
            amount={variantToShow.price.amount}
          />
        </TilePriceWrapper>
      </TileTextWrapper>
    </Tile>
  )
}

export default MultiProductTile
